import * as _ from "lodash";
import { ESRecord, IHasChangesOptions } from "./ESRecord";
import { jsdRelation, Relation_BelongsTo, Relation_HasMany, Relation_HasOne } from "./ESRecordResolver";
import { ESRecordMapper } from "./ESRecordMapper";

export enum eRecordChangeType {
    New = "New",
    Update = "Update",
    Delete = "Delete",
}

export interface IChangedValue {
    column: string;
    value: unknown;
    previous: unknown;
}

export interface IRecordChange {
    mapper: string;
    mapperUI?: string;

    id: number | string;
    lk?: string;

    type: eRecordChangeType;
    changes: IChangedValue[];
}

export interface IRecordChangesOptions {
    changeCountThreshold?: number;
    bIncludeFieldDetails?: boolean;
    bIncludeHasMany?: boolean;
    bIncludeHasOne?: boolean;
    maxDepth?: number;
    dctIncludeBelongsToNamedRelations?: { [mapper_id: string]: string[] };
    dctExcludeHasManyNamedRelations?: { [mapper_id: string]: string[] };
    dctChangeFields?: { [mapper_id: string]: string[] };
    dctExcludeFields?: { [mapper_id: string]: string[] };
    dctIgnoreChanges?: { [mapper_id: string]: { bNew?: boolean; bUpdates?: boolean; bDeletes?: boolean } };
    bIgnoreStandardFields?: boolean;
    [id: string]: any;
}

const standardFieldNames: string[] = ["DELETED_IND", "DELETED_DT", "INTEGRATION_IND"];

export class ESRecordChanges {
    //--
    static findChanges(records: ESRecord | ESRecord[], options?: IRecordChangesOptions): IRecordChange[] {
        let opts = options || {};
        _.defaults(opts, {
            bIncludeHasMany: true,
            bIncludeHasOne: true,
            dctIncludeBelongsToNamedRelations: {},
            dctExcludeHasManyNamedRelations: {},
            dctChangeFields: {},
            dctExcludeFields: {},
            dctIgnoreChanges: {},
            bIgnoreCoreFields: false,
        });

        let returnValue: IRecordChange[] = [];

        ESRecordChanges.addChanges(records, opts, returnValue, 0);

        return returnValue;
    }

    private static addChanges(
        records: ESRecord | ESRecord[],
        opts: IRecordChangesOptions,
        arrChanges: IRecordChange[],
        depth: number,
    ): void {
        if (_.isEmpty(records)) return;

        if (!_.isArray(records)) records = [records];

        if (_.isNumber(opts.maxDepth) && depth > opts.maxDepth) return;

        // Check and Log Records
        let mapper: ESRecordMapper = <any>records[0]._mapper();
        let mapperName = mapper.name;
        let changeOptions: IHasChangesOptions = {};

        let idField = mapper.idAttribute;

        let arrIncludeRels = opts.dctIncludeBelongsToNamedRelations[mapperName] || [];
        let arrExcludeRels = opts.dctExcludeHasManyNamedRelations[mapperName] || [];

        let arrChangeFields = opts.dctChangeFields[mapperName] || [];
        let arrExcludeFields = _.union(
            opts.dctExcludeFields[mapperName] || [],
            opts.bIgnoreStandardFields ? standardFieldNames : [],
        );
        let ignoreChanges = opts.dctIgnoreChanges[mapperName] || {};

        if (!_.isEmpty(arrChangeFields) || !_.isEmpty(arrExcludeFields)) {
            if (_.isEmpty(arrChangeFields)) {
                arrChangeFields = _.keys(mapper.schema.properties);
                _.pull(arrChangeFields, mapper.idAttribute);
            }

            _.pullAll(arrChangeFields, arrExcludeFields);
            changeOptions.properties = arrChangeFields;
        }

        function stopCheck(): boolean {
            if (_.isNumber(opts.changeCountThreshold)) return false;

            if (opts.changeCountThreshold >= arrChanges.length) return true;

            return false;
        }

        for (let aRecord of records) {
            let bRecurse = false;

            switch (true) {
                //-- New Record, is change, don't recurse
                case aRecord.isActiveNew:
                    if (ignoreChanges.bNew) break;

                    arrChanges.push({
                        id: (<any>aRecord)[idField],
                        type: eRecordChangeType.New,
                        changes: [],
                        mapper: mapper.name,
                    });
                    break;

                //-- Records that don't exist, and are marked deleted, aren't a change"
                case !aRecord._rowExists && !aRecord.isActive:
                    break;

                //-- Deleted Record, is change, don't recurse
                case !aRecord.isActive:
                    if (ignoreChanges.bDeletes) break;

                    //-- If soft delete, check if the delete indicator is modified.
                    if (mapper.bDeleteIndicatorExists)
                        if (!aRecord.hasChanges({ properties: [mapper.DeletedIndicatorFieldName] })) break;

                    arrChanges.push({
                        id: (<any>aRecord)[idField],
                        type: eRecordChangeType.Delete,
                        changes: [],
                        mapper: mapper.name,
                    });
                    break;

                //
                default:
                    bRecurse = true;
                    if (aRecord.hasChanges(changeOptions)) {
                        if (ignoreChanges.bUpdates) break;
                        //--
                        let updChange: IRecordChange = {
                            id: (<any>aRecord)[idField],
                            lk: mapper.getUILogicalKey(aRecord),
                            type: eRecordChangeType.Update,
                            changes: [],
                            mapper: mapper.name,
                            mapperUI: mapper.getUIName(),
                        };

                        if (opts.bIncludeFieldDetails) {
                            let changes = aRecord.getChangedProperties(changeOptions);
                            _.forEach(changes, (aChangeField) => {
                                updChange.changes.push({
                                    column: aChangeField,
                                    value: (<any>aRecord)[aChangeField],
                                    previous: aRecord.previous(aChangeField),
                                });
                            });
                        }

                        arrChanges.push(updChange);
                    }
            }

            if (stopCheck()) break;

            // Traverse Relationships
            if (bRecurse) {
                //--
                _.forEach(mapper.relationList, (rel: jsdRelation) => {
                    if (stopCheck()) return false;

                    switch (rel.type) {
                        case Relation_BelongsTo:
                            if (_.includes(arrIncludeRels, rel.relation)) {
                                ESRecordChanges.addChanges((<any>aRecord)[rel.localField], opts, arrChanges, depth);
                            }
                            break;

                        case Relation_HasMany:
                            if (!_.includes(arrExcludeRels, rel.relation)) {
                                ESRecordChanges.addChanges((<any>aRecord)[rel.localField], opts, arrChanges, depth + 1);
                            }
                            break;

                        case Relation_HasOne:
                            if (!_.includes(arrExcludeRels, rel.relation)) {
                                ESRecordChanges.addChanges((<any>aRecord)[rel.localField], opts, arrChanges, depth + 1);
                            }
                            break;
                    }
                });
            }
        }
    }
}
